import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Plus, ImageIcon, Film, Music, Send, Square, Check, Clock, ChevronDown, ChevronUp, Play, Pause, Loader2 } from 'lucide-react';
import { ReactComponent as SettingsIcon } from '../../../assets/settings.svg';
import AiSettingsPanel from '../CanvasTools/AiSettingsPanel';
import MediaSelectorModal from './MediaSelectorModal';
import CustomSlider from '../../UI/CustomSlider';
import { useAiGeneration } from '../../../hooks/useAiGeneration';
import { useUpdateAiSpriteMedia } from '../../../hooks/useUpdateAiSpriteMedia';
import { getModelById, getDefaultVoiceId, getNextImageRole, getImageRoleLabel, getImageSlots, resolveClientModel } from '../../../config/aiModels';
import { estimateCreditCost } from '../../../config/creditPricing';
import { useAiEffectiveModel } from '../../../hooks/useAiEffectiveModel';
import { useAuth } from '../../../contexts/AuthContext';

// ── Cycling status messages for each generation phase ──────────────────────
const QUEUED_MESSAGES = [
  'You\'ve been added to the queue ✨',
  'Warming up the creative engines...',
  'Your turn is coming up shortly!',
];

const PROCESSING_MESSAGES = [
  'Crafting something special for you...',
  'It\'ll be with you in a moment ⚡',
  'The magic is happening right now...',
];

const MESSAGE_CYCLE_INTERVAL = 4000; // 4 seconds between message changes

/**
 * Hook that cycles through an array of messages at a set interval.
 * Returns the current message string.
 */
const useCyclingMessage = (messages, active) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!active) {
      setIndex(0);
      return;
    }
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % messages.length);
    }, MESSAGE_CYCLE_INTERVAL);
    return () => clearInterval(timer);
  }, [active, messages]);

  return active ? messages[index] : '';
};

const MEDIA_TYPE_OPTIONS = [
  { type: 'image', label: 'Image', icon: ImageIcon },
  // { type: 'video', label: 'Video', icon: Film },
  // { type: 'audio', label: 'Audio', icon: Music },
];

// Small tag chip for displaying payload info
const PayloadTag = ({ label, value }) => (
  <div className="inline-flex items-center gap-1 bg-ag-black-600 rounded px-2 py-0.5 text-[10px] font-sourceSans text-ag-black-300">
    <span className="opacity-60">{label}:</span>
    <span className="text-white truncate max-w-[100px]">{value}</span>
  </div>
);

// Single result card — clickable to select
const ResultCard = ({ result, index, isActive, onSelect, compact = false }) => {
  const { mediaType, url, thumbnailUrl } = result;
  const previewUrl = thumbnailUrl || url;

  return (
    <button
      onClick={() => onSelect(index)}
      className={`relative rounded-lg overflow-hidden border-2 transition-colors ${
        isActive
          ? 'border-ag-green'
          : 'border-ag-black-400 hover:border-ag-black-300'
      }`}
    >
      {mediaType === 'image' && (
        <img crossOrigin='anonymous' src={previewUrl} alt={`Option ${index + 1}`} className={`w-full h-auto object-cover ${compact ? 'max-h-[80px]' : ''}`} />
      )}
      {mediaType === 'video' && (
        <video crossOrigin='anonymous' src={url} className={`w-full h-auto ${compact ? 'max-h-[80px]' : ''}`} muted />
      )}
      {mediaType === 'audio' && (
        <div className="p-2 flex items-center gap-1.5">
          <Music size={compact ? 10 : 12} className="text-ag-green" />
          <audio crossOrigin='anonymous' src={url} controls className={`flex-1 ${compact ? 'h-5' : 'h-6'}`} />
        </div>
      )}
      {isActive && (
        <div className={`absolute top-1 right-1 ${compact ? 'w-4 h-4' : 'w-5 h-5'} rounded-full bg-ag-green flex items-center justify-center`}>
          <Check size={compact ? 10 : 12} className="text-black" />
        </div>
      )}
      {!compact && (
        <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1.5 py-0.5 text-[9px] font-sourceSans text-white text-center">
          Option {index + 1}
        </div>
      )}
    </button>
  );
};

// Compact thumbnail box for a single history result — playable inline
const HistoryThumbnail = ({ result, jobId, resultIndex, isActive, onSelect, prompt, isSwapping, playingKey, onPlay }) => {
  const { mediaType, url, thumbnailUrl } = result;
  const previewUrl = thumbnailUrl || url;
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [isMediaLoaded, setIsMediaLoaded] = useState(false);

  const thumbKey = `${jobId}-${resultIndex}`;
  const isPlaying = playingKey === thumbKey;
  const isPlayable = mediaType === 'video' || mediaType === 'audio';

  // Reset loaded state when URL changes
  useEffect(() => {
    setIsMediaLoaded(false);
  }, [url, previewUrl]);

  // Pause/play media element when playingKey changes
  useEffect(() => {
    const mediaEl = mediaType === 'video' ? videoRef.current : audioRef.current;
    if (!mediaEl) return;
    if (isPlaying) {
      mediaEl.play().catch(() => {});
    } else {
      mediaEl.pause();
    }
  }, [isPlaying, mediaType]);

  const handlePlayToggle = useCallback((e) => {
    e.stopPropagation();
    onPlay(isPlaying ? null : thumbKey);
  }, [isPlaying, thumbKey, onPlay]);

  const handleMediaEnded = useCallback(() => {
    onPlay(null);
  }, [onPlay]);

  return (
    <button
      onClick={() => !isSwapping && onSelect(jobId, resultIndex)}
      className={`relative rounded-md overflow-hidden border transition-colors aspect-square ${
        isSwapping ? 'border-ag-green pointer-events-none' :
        isActive
          ? 'border-ag-green'
          : 'border-ag-black-800 hover:border-ag-black-700'
      }`}
      title={prompt || ''}
    >
      {/* Media loading spinner */}
      {!isMediaLoaded && mediaType !== 'audio' && (
        <div className="absolute inset-0 flex items-center justify-center bg-ag-black-600 z-10">
          <Loader2 size={16} className="animate-spin text-ag-black-300" />
        </div>
      )}
      {mediaType === 'image' && (
        <img crossOrigin='anonymous' src={previewUrl} alt="Generation" className="w-full h-full object-cover" onLoad={() => setIsMediaLoaded(true)} />
      )}
      {mediaType === 'video' && (
        <video
          crossOrigin='anonymous'
          ref={videoRef}
          src={url}
          poster={thumbnailUrl}
          className="w-full h-full object-cover"
          muted
          playsInline
          onEnded={handleMediaEnded}
          onLoadedData={() => setIsMediaLoaded(true)}
        />
      )}
      {mediaType === 'audio' && (
        <>
          <div className="w-full h-full flex items-center justify-center bg-ag-black-600">
            <Music size={16} className="text-ag-green" />
          </div>
          <audio crossOrigin='anonymous' ref={audioRef} src={url} onEnded={handleMediaEnded} />
        </>
      )}
      {/* Swap-in-progress overlay */}
      {isSwapping && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-20">
          <Loader2 size={16} className="animate-spin text-ag-green" />
        </div>
      )}
      {/* Play/Pause button — top right corner (video & audio only) */}
      {isPlayable && !isSwapping && (
        <div
          onClick={handlePlayToggle}
          className="absolute top-0.5 right-0.5 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center cursor-pointer hover:bg-black/80 transition-colors z-10"
        >
          {isPlaying ? (
            <Pause size={8} className="text-white" />
          ) : (
            <Play size={8} className="text-white ml-[1px]" />
          )}
        </div>
      )}
      {isActive && !isSwapping && (
        <div className="absolute top-0.5 left-0.5 w-3.5 h-3.5 rounded-full bg-ag-green flex items-center justify-center z-10">
          <Check size={8} className="text-black" />
        </div>
      )}
    </button>
  );
};

const AiSpriteProperties = ({ element, onPropertyChange, onSliderStart, onSliderEnd }) => {
  const aiPayload = element?.componentData?.aiPayload;
  const spriteId = element?.id;

  // Embedded generation history from template (if this sprite was loaded from a template)
  const embeddedHistory = element?.componentData?.aiGenerationHistory || null;

  // AI generation hook — manages backend polling, selection & history
  // Pass embeddedHistory so it can be used as fallback when no API jobs exist for this user
  const {
    job,
    isGenerating,
    error: generationError,
    results,
    selectedResultIndex,
    history,
    showingAllHistory,
    generate,
    stopGeneration,
    selectResult,
    fetchAllHistory,
    collapseHistory,
  } = useAiGeneration(spriteId, embeddedHistory);

  // Derive display status for cycling messages
  const jobStatus = job?.status || (isGenerating ? 'pending' : null);
  const isQueued = jobStatus === 'pending';
  const isProcessing = jobStatus === 'processing';

  // Cycling status messages
  const queuedMessage = useCyclingMessage(QUEUED_MESSAGES, isQueued);
  const processingMessage = useCyclingMessage(PROCESSING_MESSAGES, isProcessing);
  const statusMessage = isQueued ? queuedMessage : isProcessing ? processingMessage : '';

  const { user } = useAuth();

  // Hook to update the sprite's visual media while keeping type:'ai'
  const updateAiSpriteMedia = useUpdateAiSpriteMedia();

  // Track which thumbnail is currently being swapped in
  const [swappingKey, setSwappingKey] = useState(null);

  // Only one thumbnail plays at a time
  const [playingKey, setPlayingKey] = useState(null);

  // AI dialog state (embedded in properties panel)
  const settingsPanelRef = useRef(null);
  const mediaSelectorRef = useRef(null);

  const [settingsOpen, setSettingsOpen] = useState(true);
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  const [mediaSelectorOpen, setMediaSelectorOpen] = useState(false);
  const [prompt, setPrompt] = useState(aiPayload?.prompt || '');
  const [referenceMediaList, setReferenceMediaList] = useState(aiPayload?.referenceMedia || []);

  // Settings state — initialize from existing payload if available
  const [selectedModel, setSelectedModel] = useState(resolveClientModel(aiPayload?.settings) || 'kling-silent');
  const [aspectRatio, setAspectRatio] = useState(aiPayload?.settings?.aspectRatio || '16:9');
  const [outputCount, setOutputCount] = useState(aiPayload?.settings?.outputCount || 1);
  const [length, setLength] = useState(aiPayload?.settings?.length || '5s');
  const [selectedVoiceId, setSelectedVoiceId] = useState(aiPayload?.settings?.voiceId || getDefaultVoiceId());

  // Inferred model + hint (centralized hook)
  const { effectiveModel, modelHint, handleModelChange } = useAiEffectiveModel(
    selectedModel, prompt, aspectRatio, setSelectedModel
  );

  // Sync settings, prompt & reference media when element changes
  useEffect(() => {
    setSelectedModel(resolveClientModel(aiPayload?.settings) || 'kling-silent');
    setAspectRatio(aiPayload?.settings?.aspectRatio || '16:9');
    setOutputCount(aiPayload?.settings?.outputCount || 1);
    setLength(aiPayload?.settings?.length || '5s');
    setSelectedVoiceId(aiPayload?.settings?.voiceId || getDefaultVoiceId());
    setPrompt(aiPayload?.prompt || '');
    setReferenceMediaList(aiPayload?.referenceMedia || []);
  }, [spriteId, aiPayload]);

  // When user selects a result (from current job or history), update the sprite
  // media while preserving the AI sprite type
  const handleSelectResult = useCallback(async (jobId, index) => {
    const key = `${jobId}-${index}`;
    setSwappingKey(key);
    try {
      const selection = await selectResult(jobId, index);
      if (selection) {
        // Find the source job so we can update the element's aiPayload
        // to reflect the selected generation's prompt/settings
        const sourceJob = job?._id === jobId ? job : history.find(h => h._id === jobId);
        const newAiPayload = sourceJob ? {
          prompt: sourceJob.prompt,
          settings: sourceJob.settings,
          referenceMedia: sourceJob.referenceMedia,
        } : null;

        // Use the AI-specific media updater that preserves type:'ai'
        await updateAiSpriteMedia(
          element.id,
          selection.selectedResult.url,
          selection.selectedResult.mediaType,
          selection.media.originalName,
          selection.selectedResult.thumbnailUrl,
          newAiPayload
        );
      }
    } finally {
      setSwappingKey(null);
    }
  }, [selectResult, updateAiSpriteMedia, element?.id, job, history]);

  // Handler for current job results (uses job._id)
  const handleSelectCurrentResult = useCallback(async (index) => {
    if (!job?._id) return;
    await handleSelectResult(job._id, index);
  }, [job, handleSelectResult]);

  // Handle media type selection from the + menu.
  // For images, auto-assign the correct role based on the model's imageSlots config.
  const handleMediaTypeSelect = useCallback((mediaType) => {
    setPlusMenuOpen(false);
    if (mediaSelectorRef.current) {
      setMediaSelectorOpen(true);
      mediaSelectorRef.current.open(mediaType, null, (result, name) => {
        const url = typeof result === 'string' ? result : result?.url;
        const thumbnailUrl = typeof result === 'object' ? result?.thumbnailUrl : null;
        if (url) {
          setReferenceMediaList((prev) => {
            // For images, auto-assign the role from the model's imageSlots
            let role = null;
            if (mediaType === 'image') {
              role = getNextImageRole(effectiveModel, prev);
            }
            return [
              ...prev,
              { type: mediaType, url, name: name || '', thumbnailUrl, ...(role && { role }) },
            ];
          });
        }
        setMediaSelectorOpen(false);
      });
    }
  }, [effectiveModel]);

  const handleRemoveReference = useCallback((index) => {
    setReferenceMediaList((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Regenerate — sends a new generation request via the hook
  const handleGenerate = useCallback(() => {
    if (!prompt.trim()) return;
    if (isGenerating) return; // Block while in-flight

    // Look up the model config to determine which settings to include
    const modelConfig = getModelById(effectiveModel);

    // Build settings — only include what the model supports
    const settings = { model: effectiveModel };

    if (modelConfig?.settings.aspectRatio) {
      settings.aspectRatio = aspectRatio;
    }
    if (modelConfig?.settings.outputCount) {
      settings.outputCount = outputCount;
    }
    if (modelConfig?.settings.length) {
      settings.length = length;
    }
    if (modelConfig?.settings.voices) {
      settings.voiceId = selectedVoiceId;
    }

    const payload = {
      prompt: prompt.trim(),
      settings,
      ...(referenceMediaList.length > 0 && { referenceMedia: referenceMediaList }),
    };

    generate(payload);

    // Clear prompt after sending
    setPrompt('');
    setReferenceMediaList([]);
    setSettingsOpen(false);
    setPlusMenuOpen(false);
  }, [prompt, effectiveModel, aspectRatio, outputCount, length, selectedVoiceId, referenceMediaList, isGenerating, generate]);

  if (!aiPayload) return null;

  // The URL currently active on the canvas sprite
  const activeUrl = element?.componentData?.url || '';

  // Build history list: exclude the current active/in-progress job from history
  // to avoid duplication with the "current results" section
  const currentJobId = job?._id;
  const historyJobs = history.filter((h) => h._id !== currentJobId);

  return (
    <div className="space-y-3">
      {/* Chat Section Header */}
      <div className="flex items-center gap-2">
        <h4 className="text-xs font-sourceSans font-semibold text-white uppercase tracking-wider">Chat</h4>
        {isGenerating && (
          <div className="w-2 h-2 rounded-full bg-ag-green animate-pulse" title="Generating..." />
        )}
      </div>

      {/* Payload Tags — small boxes showing what was used to generate */}
      {/* <div className="flex flex-wrap gap-1.5 bg-ag-black-600 rounded-lg px-3 py-2">
        {aiPayload.prompt && (
          <PayloadTag label="prompt" value={aiPayload.prompt} />
        )}
        {aiPayload.settings?.model && (
          <PayloadTag label="model" value={aiPayload.settings.model} />
        )}
        {aiPayload.settings?.aspectRatio && (
          <PayloadTag label="ratio" value={aiPayload.settings.aspectRatio} />
        )}
        {aiPayload.settings?.length && (
          <PayloadTag label="length" value={aiPayload.settings.length} />
        )}
        {aiPayload.settings?.voiceId && (
          <PayloadTag label="voice" value={aiPayload.settings.voiceId} />
        )}
        {aiPayload.referenceMedia?.map((media, i) => (
          <PayloadTag key={i} label={media.type} value={media.name || 'media'} />
        ))}
      </div> */}

      {/* Generation Status / Results */}
      {generationError && (
        <div className="rounded-lg bg-red-900/30 border border-red-700/50 p-3 text-[11px] font-sourceSans text-red-300 text-center">
          Generation failed. Try again
        </div>
      )}

      {(jobStatus === 'pending' || jobStatus === 'processing') && (
        <div className="rounded-lg bg-ag-black-600 p-4 flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-ag-black-500 flex items-center justify-center">
            <div className="w-3 h-3 border-2 border-ag-green border-t-transparent rounded-full animate-spin" />
          </div>
          <span className="text-[11px] font-sourceSans text-white text-center transition-opacity duration-500">
            {statusMessage}
          </span>
          <button
            onClick={stopGeneration}
            className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-ag-black-500 hover:bg-red-900/40 text-[10px] font-sourceSans text-ag-black-300 hover:text-red-300 transition-colors"
          >
            <Square size={8} className="fill-current" />
            Stop generating
          </button>
        </div>
      )}

      {/* All generations — unified compact 3-column grid */}
      {(results.length > 0 || historyJobs.length > 0) && (
        <div className="space-y-1.5">
          <h4 className="text-[11px] font-sourceSans font-semibold text-ag-black-300 uppercase tracking-wider">
            tap to use
          </h4>
          <div className="grid grid-cols-3 gap-1.5">
            {/* Current job results first */}
            {results.map((result, index) => (
              <HistoryThumbnail
                key={`current-${index}`}
                result={result}
                jobId={job?._id}
                resultIndex={index}
                isActive={activeUrl && result.url === activeUrl}
                onSelect={handleSelectResult}
                prompt={job?.prompt || ''}
                isSwapping={swappingKey === `${job?._id}-${index}`}
                playingKey={playingKey}
                onPlay={setPlayingKey}
              />
            ))}
            {/* Then previous generation results */}
            {historyJobs.flatMap((historyJob) =>
              (historyJob.results || []).map((result, rIdx) => (
                <HistoryThumbnail
                  key={`${historyJob._id}-${rIdx}`}
                  result={result}
                  jobId={historyJob._id}
                  resultIndex={rIdx}
                  isActive={activeUrl && result.url === activeUrl}
                  onSelect={handleSelectResult}
                  prompt={historyJob.prompt}
                  isSwapping={swappingKey === `${historyJob._id}-${rIdx}`}
                  playingKey={playingKey}
                  onPlay={setPlayingKey}
                />
              ))
            )}
          </div>
          {/* Show all / Show less toggle */}
          {!showingAllHistory ? (
            <button
              onClick={fetchAllHistory}
              className="text-[10px] font-sourceSans text-ag-black-400 hover:text-ag-green transition-colors mt-1"
            >
              Show all generations
            </button>
          ) : (
            <button
              onClick={collapseHistory}
              className="text-[10px] font-sourceSans text-ag-black-400 hover:text-ag-green transition-colors mt-1"
            >
              Show less
            </button>
          )}
        </div>
      )}

      {/* Embedded AI Dialog for re-generation */}
      <div className="rounded-lg bg-black overflow-visible">
        {/* Settings Panel (opens above) */}
        {settingsOpen && (
          <div className="relative">
            <div ref={settingsPanelRef} className="bg-ag-black-600 rounded-lg">
              <AiSettingsPanel
                isOpen={true}
                selectedModel={selectedModel}
                onModelChange={handleModelChange}
                aspectRatio={aspectRatio}
                onAspectRatioChange={setAspectRatio}
                outputCount={outputCount}
                onOutputCountChange={setOutputCount}
                length={length}
                onLengthChange={setLength}
                selectedVoiceId={selectedVoiceId}
                onVoiceIdChange={setSelectedVoiceId}
                inline
                compact
              />
            </div>
          </div>
        )}

        {/* Reference media chips */}
        {referenceMediaList.length > 0 && (
          <div className="flex items-center gap-1.5 px-2 pt-2 pb-1 flex-wrap">
            {referenceMediaList.map((media, index) => (
              <div
                key={index}
                className="relative group flex flex-col items-center bg-ag-black-600 rounded overflow-hidden"
                style={{ width: 48 }}
              >
                {/* Thumbnail preview */}
                {media.type === 'image' ? (
                  <img
                    crossOrigin="anonymous"
                    src={media.thumbnailUrl || media.url}
                    alt={media.name || 'Reference'}
                    className="w-12 h-12 object-cover"
                  />
                ) : media.type === 'video' ? (
                  media.thumbnailUrl ? (
                    <img
                      crossOrigin="anonymous"
                      src={media.thumbnailUrl}
                      alt={media.name || 'Reference'}
                      className="w-12 h-12 object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 flex items-center justify-center bg-ag-black-500">
                      <Film size={16} className="text-ag-black-300" />
                    </div>
                  )
                ) : (
                  <div className="w-12 h-12 flex items-center justify-center bg-ag-black-500">
                    <Music size={16} className="text-ag-black-300" />
                  </div>
                )}
                {/* Role label overlay */}
                {media.role && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-0.5 py-px text-center">
                    <span className="text-[8px] font-sourceSans text-ag-green font-semibold leading-none">
                      {getImageRoleLabel(effectiveModel, media.role)}
                    </span>
                  </div>
                )}
                {/* Remove button */}
                <button
                  onClick={() => handleRemoveReference(index)}
                  className="absolute top-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-black/60 text-ag-black-300 hover:text-white hover:bg-black/80 transition-colors flex items-center justify-center text-[9px] leading-none opacity-0 group-hover:opacity-100"
                >
                  ×
                </button>
              </div>
            ))}
            {/* Next available image slot hint — e.g. "+ Last frame" for Kling after first frame is added */}
            {!isGenerating && (() => {
              const nextRole = getNextImageRole(effectiveModel, referenceMediaList);
              if (!nextRole) return null;
              const label = getImageRoleLabel(effectiveModel, nextRole);
              return (
                <button
                  onClick={() => handleMediaTypeSelect('image')}
                  className="flex items-center gap-1 bg-ag-black-600 hover:bg-ag-black-500 rounded px-1.5 py-0.5 text-[10px] font-sourceSans text-ag-black-300 hover:text-white transition-colors border border-dashed border-ag-black-400"
                >
                  <ImageIcon size={10} />
                  <span>+ {label}</span>
                </button>
              );
            })()}
          </div>
        )}

        {/* Image slot hints — shown when no media is attached yet but model accepts images */}
        {referenceMediaList.length === 0 && !isGenerating && getImageSlots(effectiveModel).length > 0 && (
          <div className="flex items-center gap-1.5 px-2 pt-1 pb-1">
            {getImageSlots(effectiveModel).map((slot) => (
              <button
                key={slot.role}
                onClick={() => handleMediaTypeSelect('image')}
                className="flex items-center gap-1 px-2 py-0.5 rounded bg-ag-black-600 hover:bg-ag-black-500 text-[10px] font-sourceSans text-ag-black-300 hover:text-white transition-colors"
              >
                <ImageIcon size={10} />
                + {slot.label}
              </button>
            ))}
          </div>
        )}

        {/* Input row */}
        <div className="px-1.5 pt-1.5 pb-0.5">
          <input
            type="text"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !isGenerating) handleGenerate(); }}
            placeholder={isGenerating ? statusMessage : 'Edit or regenerate...'}
            disabled={isGenerating}
            className={`w-full bg-transparent text-xs font-sourceSans text-white placeholder-gray-500 outline-none italic ${isGenerating ? 'opacity-30 cursor-not-allowed' : ''}`}
          />
        </div>

        {/* Controls row: +, settings, hint, send */}
        <div className="flex items-center gap-1.5 px-1.5 py-1">
          {/* Plus button */}
          <div className="relative">
            <button
              onClick={() => setPlusMenuOpen((prev) => !prev)}
              disabled={isGenerating}
              className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded transition-colors ${
                isGenerating ? 'opacity-30 cursor-not-allowed' :
                plusMenuOpen ? 'bg-ag-black-500' : 'hover:bg-ag-black-500'
              }`}
            >
              <Plus size={14} className="text-white" />
            </button>

            {plusMenuOpen && !isGenerating && (
              <div className="absolute bottom-full mb-1 left-0 z-[60] bg-ag-black-700 border border-ag-black-600 rounded-lg shadow-2xl overflow-hidden min-w-[120px]">
                {MEDIA_TYPE_OPTIONS.map(({ type, label, icon: Icon }) => (
                  <button
                    key={type}
                    onClick={() => handleMediaTypeSelect(type)}
                    className="flex items-center gap-2 w-full px-2.5 py-2 text-xs font-sourceSans text-ag-black-300 hover:bg-ag-black-600 hover:text-white transition-colors"
                  >
                    <Icon size={12} className="opacity-60" />
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Settings icon */}
          <button
            onClick={() => setSettingsOpen((prev) => !prev)}
            disabled={isGenerating}
            className={`flex-shrink-0 p-0.5 transition-opacity hover:opacity-80 ${isGenerating ? 'opacity-30 cursor-not-allowed' : ''}`}
          >
            <SettingsIcon
              className={`w-[11px] h-[9px] transition-colors ${
                settingsOpen
                  ? '[&_circle]:stroke-white [&_path]:stroke-white'
                  : '[&_circle]:stroke-[#4A4A4A] [&_path]:stroke-[#4A4A4A]'
              }`}
            />
          </button>

          {/* Model hint */}
          {modelHint && !isGenerating && (
            <span className="flex-1 text-[11px] font-sourceSans text-ag-black-400 truncate" title={modelHint}>
              {modelHint}
            </span>
          )}

          {/* Credit cost + Send / Stop button */}
          {isGenerating ? (
            <button
              onClick={stopGeneration}
              className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded bg-red-600 hover:bg-red-500 transition-colors ml-auto"
              title="Stop generating"
            >
              <Square size={10} className="text-white fill-white" />
            </button>
          ) : (() => {
            const cost = estimateCreditCost(effectiveModel, { prompt, length, outputCount });
            const credits = user?.credits ?? 0;
            const canAfford = credits >= (cost || 0);
            return (
              <>
                {cost != null && (
                  <span className={`text-[11px] font-sourceSans whitespace-nowrap ml-auto ${canAfford ? 'text-ag-green/70' : 'text-red-400'}`}>
                    ⚡ {cost} {cost === 1 ? 'credit' : 'credits'}
                  </span>
                )}
                {canAfford ? (
                  <button
                    onClick={handleGenerate}
                    disabled={!prompt.trim()}
                    className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded bg-ag-green hover:bg-ag-green/90 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <Send size={12} className="text-black" />
                  </button>
                ) : (
                  <a
                    href="/manage-subscription"
                    className="flex-shrink-0 px-3 py-1 bg-red-600 text-white text-[10px] font-sourceSans font-semibold rounded hover:bg-red-500 transition-colors whitespace-nowrap text-center"
                  >
                    Upgrade
                  </a>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* ── General Properties (opacity, volume, mute, playback) ── */}
      {onPropertyChange && (() => {
        const hasVideo = element.pixiSprite?.videoElement;
        const hasAudio = element.pixiSprite?.soundId;
        const hasMediaControls = hasVideo || hasAudio;

        return (
          <div className="space-y-3 pt-3 border-t border-ag-black-600">
            <h4 className="text-[11px] font-sourceSans font-semibold text-ag-black-300 uppercase tracking-wider">
              Properties
            </h4>

            <CustomSlider
              label="Opacity"
              value={(element.alpha !== undefined ? element.alpha : 1) * 100}
              onChange={(value) => onPropertyChange('alpha', value / 100)}
              min={0}
              max={100}
              valueFormatter={(val) => `${Math.round(val)}%`}
              onSliderStart={onSliderStart}
              onSliderEnd={onSliderEnd}
            />

            {hasMediaControls && (
              <>
                <CustomSlider
                  label="Volume"
                  value={element.muted ? 0 : ((element.volume !== undefined ? element.volume : 1) * 100)}
                  onChange={(value) => {
                    const newVolume = value / 100;
                    onPropertyChange('volume', newVolume);
                    if (newVolume > 0 && element.muted) {
                      onPropertyChange('muted', false);
                    }
                  }}
                  min={0}
                  max={100}
                  valueFormatter={(val) => `${Math.round(val)}%`}
                  onSliderStart={onSliderStart}
                  onSliderEnd={onSliderEnd}
                />

                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-white">Mute</label>
                  <button
                    onClick={() => onPropertyChange('muted', !element.muted)}
                    className={`px-3 py-1 text-xs rounded transition-colors ${element.muted
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-ag-green hover:bg-green-600 text-black'
                    }`}
                  >
                    {element.muted ? 'Unmute' : 'Mute'}
                  </button>
                </div>

                <div className="flex flex-col gap-4">
                  <label className="text-xs font-medium text-white">Frame Properties</label>
                  <div className="flex flex-col space-y-4">
                    <label className="flex items-center space-x-2 text-xs text-white">
                      <input
                        type="radio"
                        name="ai-playbackMode"
                        value="pause"
                        checked={element.playbackMode === 'pause'}
                        onChange={() => onPropertyChange('playbackMode', 'pause')}
                        className="h-4 w-4 rounded-full accent-ag-green"
                      />
                      <span>Pause at last frame</span>
                    </label>
                    <label className="flex items-center space-x-2 text-xs text-white">
                      <input
                        type="radio"
                        name="ai-playbackMode"
                        value="loop"
                        checked={element.playbackMode === 'loop' || !element.playbackMode}
                        onChange={() => onPropertyChange('playbackMode', 'loop')}
                        className="h-4 w-4 rounded-full accent-ag-green"
                      />
                      <span>Loop</span>
                    </label>
                  </div>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* Media Selector Modal */}
      {ReactDOM.createPortal(
        <MediaSelectorModal ref={mediaSelectorRef} onClose={() => setMediaSelectorOpen(false)} />,
        document.body
      )}
    </div>
  );
};

export default AiSpriteProperties;
